/**
 * RPE Percentage Chart
 * Based on Reaction Training Systems / standard powerlifting charts
 * Maps Reps (rows) and RPE (columns) to %1RM
 */
export const RPE_CHART: Record<number, Record<number, number>> = {
  1: {
    10: 100,
    9.5: 97.8,
    9: 95.5,
    8.5: 93.9,
    8: 92.2,
    7.5: 90.7,
    7: 89.2,
    6.5: 87.8,
    6: 86.3,
  },
  2: {
    10: 95.5,
    9.5: 93.9,
    9: 92.2,
    8.5: 90.7,
    8: 89.2,
    7.5: 87.8,
    7: 86.3,
    6.5: 84.9,
    6: 83.5,
  },
  3: {
    10: 92.2,
    9.5: 90.7,
    9: 89.2,
    8.5: 87.8,
    8: 86.3,
    7.5: 84.9,
    7: 83.5,
    6.5: 82.2,
    6: 80.8,
  },
  4: {
    10: 89.2,
    9.5: 87.8,
    9: 86.3,
    8.5: 84.9,
    8: 83.5,
    7.5: 82.2,
    7: 80.8,
    6.5: 79.5,
    6: 78.2,
  },
  5: {
    10: 86.3,
    9.5: 84.9,
    9: 83.5,
    8.5: 82.2,
    8: 80.8,
    7.5: 79.5,
    7: 78.2,
    6.5: 76.9,
    6: 75.6,
  },
  6: {
    10: 83.7,
    9.5: 82.4,
    9: 81.1,
    8.5: 79.9,
    8: 78.6,
    7.5: 77.4,
    7: 76.2,
    6.5: 75.0,
    6: 73.8,
  },
  7: {
    10: 81.1,
    9.5: 79.9,
    9: 78.6,
    8.5: 77.4,
    8: 76.2,
    7.5: 75.0,
    7: 73.9,
    6.5: 72.8,
    6: 71.6,
  },
  8: {
    10: 78.6,
    9.5: 77.4,
    9: 76.2,
    8.5: 75.1,
    8: 73.9,
    7.5: 72.8,
    7: 71.7,
    6.5: 70.6,
    6: 69.5,
  },
  9: {
    10: 76.2,
    9.5: 75.1,
    9: 73.9,
    8.5: 72.8,
    8: 71.7,
    7.5: 70.7,
    7: 69.6,
    6.5: 68.6,
    6: 67.6,
  },
  10: {
    10: 73.9,
    9.5: 72.8,
    9: 71.8,
    8.5: 70.7,
    8: 69.7,
    7.5: 68.7,
    7: 67.7,
    6.5: 66.7,
    6: 65.8,
  },
  11: {
    10: 70.7,
    9.5: 69.7,
    9: 68.7,
    8.5: 67.7,
    8: 66.7,
    7.5: 65.8,
    7: 64.9,
    6.5: 63.9,
    6: 63.0,
  },
  12: {
    10: 67.7,
    9.5: 66.7,
    9: 65.8,
    8.5: 64.9,
    8: 64.0,
    7.5: 63.1,
    7: 62.2,
    6.5: 61.3,
    6: 60.5,
  },
};

/**
 * Calculates a target weight based on 1RM, Reps, and RPE.
 */
export function calculateSmartWeight(
  oneRM: number | null | undefined,
  repsStr: string,
  rpe: number | null | undefined,
): number | null {
  if (!oneRM || !rpe) return null;

  // Try to parse reps as a single number (handle "5", "8-10" by taking lower bound, "AMRAP" as null)
  let reps = 0;
  if (!isNaN(Number(repsStr))) {
    reps = Number(repsStr);
  } else if (repsStr.includes("-")) {
    // For range like "8-10", use the average or lower bound? Let's use lower bound (8) to be safe/conservative
    reps = Number(repsStr.split("-")[0]);
  } else {
    // Non-numeric reps (AMRAP etc)
    return null;
  }

  // Cap reps to chart limits (1-12)
  if (reps < 1) reps = 1;
  if (reps > 12) reps = 12; // Charts usually reliable up to 10-12

  // RPE rounding to nearest 0.5
  const roundedRPE = Math.round(rpe * 2) / 2;

  if (roundedRPE < 6) return null; // Usually charts start at RPE 6
  if (roundedRPE > 10) return null;

  const percentage = RPE_CHART[reps]?.[roundedRPE];

  if (!percentage) return null;

  // Calculate weight and round to nearest 2.5kg (standard plate increments)
  const rawWeight = oneRM * (percentage / 100);
  return Math.round(rawWeight / 2.5) * 2.5;
}
